import { useEffect, useRef, useState } from "react";
import { ImageCache, preloadAndCacheImage } from "../utils/imageCache";

interface UseImagePreloaderOptions {
  priority?: boolean;
  maxConcurrent?: number;
  timeout?: number;
}

interface PreloadResult {
  loaded: string[];
  failed: string[];
  loading: string[];
  preloadImages: (urls: string[]) => void;
  isLoading: boolean;
}

const useImagePreloader = (
  initialUrls: string[] = [],
  options: UseImagePreloaderOptions = {}
): PreloadResult => {
  const { priority = false, maxConcurrent = 3, timeout = 10000 } = options;

  const [loaded, setLoaded] = useState<string[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [loading, setLoading] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const queueRef = useRef<string[]>([]);
  const activeRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const preloadImage = async (url: string): Promise<void> => {
    // Check if already in persistent cache
    if (ImageCache.has(url)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      const abortController = new AbortController();

      abortControllersRef.current.set(url, abortController);

      const timeoutId = setTimeout(() => {
        abortController.abort();
        reject(new Error("Image load timeout"));
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        abortControllersRef.current.delete(url);
        ImageCache.set(url);
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        abortControllersRef.current.delete(url);
        reject(new Error("Image load failed"));
      };

      abortController.signal.addEventListener("abort", () => {
        clearTimeout(timeoutId);
        reject(new Error("Image load aborted"));
      });

      img.src = url;

      // Check if already complete (browser cache)
      if (img.complete && img.naturalHeight > 0) {
        clearTimeout(timeoutId);
        abortControllersRef.current.delete(url);
        ImageCache.set(url);
        resolve();
      }
    });
  };

  const processQueue = async () => {
    while (
      queueRef.current.length > 0 &&
      activeRef.current.size < maxConcurrent
    ) {
      const url = queueRef.current.shift();
      if (
        !url ||
        activeRef.current.has(url) ||
        loaded.includes(url) ||
        failed.includes(url)
      ) {
        continue;
      }

      activeRef.current.add(url);
      setLoading((prev) => [...prev, url]);

      try {
        await preloadImage(url);
        setLoaded((prev) => [...prev, url]);
      } catch (error) {
        setFailed((prev) => [...prev, url]);
      } finally {
        activeRef.current.delete(url);
        setLoading((prev) => prev.filter((loadingUrl) => loadingUrl !== url));
      }
    }

    setIsLoading(queueRef.current.length > 0 || activeRef.current.size > 0);
  };

  const preloadImages = (urls: string[]) => {
    const newUrls = urls.filter(
      (url) =>
        url &&
        !ImageCache.has(url) && // Check persistent cache first
        !loaded.includes(url) &&
        !failed.includes(url) &&
        !queueRef.current.includes(url) &&
        !activeRef.current.has(url)
    );

    if (newUrls.length === 0) return;

    if (priority) {
      queueRef.current.unshift(...newUrls);
    } else {
      queueRef.current.push(...newUrls);
    }

    setIsLoading(true);
    processQueue();
  };

  useEffect(() => {
    if (initialUrls.length > 0) {
      preloadImages(initialUrls);
    }

    return () => {
      // Cleanup: abort all ongoing requests
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    processQueue();
  }, [loading.length]);

  return {
    loaded,
    failed,
    loading,
    preloadImages,
    isLoading,
  };
};

export default useImagePreloader;
